/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { Flex } from 'antd';
import { RcFile } from 'antd/es/upload';
import { useFormik } from 'formik';

import { getItRequisitionTypeOptions } from '@Features/EmployeeSelfService/requests/ITRequisition/store/selectors/itRequisitionSelector';
import { NotificationContext } from '@Context/NotificationContext';
import {
  useCancelITRequestMutation,
  useDeleteITRequestMutation,
  useGetITRequestFormQuery,
  usePostITRequestFormMutation,
  useSubmitRequestMutation,
  useValidateCugMobileMutation,
} from '@Features/EmployeeSelfService/requests/ITRequisition/store/api/itRequisitionApiSlice';
import {
  useDeleteDocumentByIdMutation,
  useFormListMutation,
  useFormUploadMutation,
} from '@Store/api/dmsApiSlice';
import {
  useAppDispatch,
  useAppSelector,
  useHasAccessToSubmit,
  useNavigationToListRequests,
  useRequisitionInfoExistingReq,
} from '@Hooks/index';
import { RcCustomRequestOptions } from '@Components/types/UploadType';
import {
  getITAccessories,
  getOrganizationOptions,
  getSIMCapValueDictSelector,
} from '@Store/selectors/dictionarySelector';
import { currentFileUploadSelector } from '@Store/selectors/fileUploadSelector';
import {
  currentSelectedUserSelector,
  currentUserSelector,
} from '@Store/selectors/userInfoSelector';
import { DocumentFormUploadResponse, RequestError } from '@Types/api/form';
import {
  transformDictToOptions,
  transformDictToOptionsNum,
  transformProjectsToOptions,
} from '@Utils/transformToOptions';
import { splitUserName } from '@Utils/splitUserName';
import { useGetUserInfoMutation } from '@Store/api/userApiSlice';
import { userInfoActions } from '@Store/slices/userInfoSlice';
import { fileUploadActions } from '@Store/slices/fileUploadSlice';
import { generateFilepath } from '@Utils/generateFilepath';
import { ItRequisitionTypeCodes } from '@Enum/api/ItRequisitionTypeCodes.enum';

import { FooterActions } from '@Enum/index';
import { itRequisitionActions } from '@Features/EmployeeSelfService/requests/ITRequisition/store/ITRequisitionSlice';
import { Attachment } from '@Components/types/Attachment';
import { getDocument } from '@Features/utils/document';
import { getFilepath } from '@Features/utils/getFilepath';
import { useGetProjectsForOrgMutation } from '@Store/api/dictionaryApiSlice';
import { Option } from '@Components/types/options';

import { MainForm, RequiredNotification } from '@Components/index';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import { ModuleOptions } from '@Features/EmployeeSelfService/requests/ITRequisition/fieldsOptions';
import { getEditFormType } from '@Features/utils/getEditFormType';
import { ItRequisitionStatusCodes } from '@Enum/api/ItRequisitionStatusCodes';
import { isMobileNumberValid } from '@Utils/isMobileNumberValid';
import { validateForm } from '@Features/utils/validateForm';
import { User } from '@Types/index';
import dayjs from 'dayjs';
import RequestDetailsSection from '../components/RequestDetailsSection/RequestDetailsSection';
import { ITRequestFormValues, IsNewUser } from '../type';
import { initialValuesForm } from '../initialValues';
import {
  getDocumentFolder,
  getFormUploadFileMetaData,
} from '../utils/document';
import { getItRequisitionFormSchema } from '../validationSchema';
import {
  getAccessoriesValue,
  getPersonalMobileValue,
  getUsernameValue,
  getValidationCugError,
  parseIntCalls,
  parseSpecificFields,
} from '../utils';
import { prepareFormHeaderData } from '../utils/prepareFormHeaderData';

const descriptionAlert = (
  <div>
    Note:
    <ol type="1">
      <li>
        Any Unofficial international calls will be deducted from the
        Employee(Requester have to advise the Employee while handing over the
        SIM card)
      </li>
      <li>For SIM Card: 0 (Default) means unlimited CUG only</li>
      <li>
        Kindly discuss with your project/department manager for SIM card credit
        limit other than 0 (Default)
      </li>
      <li>
        If Current Location is wrong in Employee Basic information, Kindly
        inform to the Payroll/HR Assistant before creating the request *
      </li>
      <li>
        Kindly discuss with employee and select the desired email ID ! Before
        creating request *
      </li>
    </ol>
  </div>
);

const ITRequestEditForm: React.FC = () => {
  const {
    editSuccessNotification,
    errorNotification,
    deleteSuccessNotification,
    submitSuccessNotification,
    cancelSuccessNotification,
  } = useContext(NotificationContext);
  const { requestId } = useParams();
  const dispatch = useAppDispatch();

  const organizations = useAppSelector(getOrganizationOptions);
  const currentUser = useAppSelector(currentUserSelector);
  const selectedUser = useAppSelector(currentSelectedUserSelector);
  const currentFileUpload = useAppSelector(currentFileUploadSelector);
  const itReqTypeOptions = useAppSelector(getItRequisitionTypeOptions);
  const simCapValues = useAppSelector(getSIMCapValueDictSelector);
  const itAccessories = useAppSelector(getITAccessories);
  const { handleBackToListRequests } = useNavigationToListRequests();

  const [cancelRequest] = useCancelITRequestMutation();
  const [submitForm] = useSubmitRequestMutation();
  const [deleteDocument] = useDeleteDocumentByIdMutation();
  const [getFormList] = useFormListMutation();
  const [postITRequestForm] = usePostITRequestFormMutation();
  const [validateCugMobile] = useValidateCugMobileMutation();
  const [formUpload] = useFormUploadMutation();
  const [getUser] = useGetUserInfoMutation();
  const [deleteRequest] = useDeleteITRequestMutation();
  const { data: editDataForm, refetch } = useGetITRequestFormQuery(requestId!, {
    refetchOnMountOrArgChange: true,
  });
  const [getProjects] = useGetProjectsForOrgMutation();

  const formHeaderData = useMemo(() => {
    const output = prepareFormHeaderData(editDataForm);
    return output;
  }, [editDataForm]);
  const requisitionInfo = useRequisitionInfoExistingReq(
    formHeaderData,
    'ITRequisition'
  );

  const [isModalOpened, setIsModalOpened] = useState(false);
  const [modalContext, setModalContext] = useState<null | 'save' | 'submit'>(
    null
  );
  const [projects, setProjects] = useState<Option[]>([]);
  const [uploadedFile, setUploadedFile] = useState<Attachment | undefined>(
    undefined
  );
  const [showDeleteNotification, setShowDeleteNotification] = useState(false);
  const [removeUploadedFile, setRemoveUploadedFile] = useState(false);
  const [disabledSaveForm, setDisableSaveForm] = useState(false);
  const [splittedUserName, setSplittedUserName] = useState<Array<string>>(['']);
  const [initialFormValues, setInitialFormValues] = useState(initialValuesForm);
  const [itReqType, setItReqType] = useState<number | null>(null);
  const [selectedEmpUser, setSelectedEmpUser] = useState<User>();
  const hasAccessToSubmit = useHasAccessToSubmit('ITRequisition');

  useLayoutEffect(() => {
    if (!hasAccessToSubmit) {
      setDisableSaveForm(true);
    }
  }, [hasAccessToSubmit]);

  const chosenUser = useMemo(() => {
    if (selectedUser?.empId) {
      return selectedUser;
    }
    return currentUser;
  }, [currentUser, selectedUser]);

  useEffect(
    () => () => {
      dispatch(fileUploadActions.setFile(null));
      dispatch(itRequisitionActions.setITRequisition(null));
      dispatch(userInfoActions.setSelectedUser(null));
    },
    [requestId]
  );

  useEffect(() => {
    setUploadedFile(undefined);
    setRemoveUploadedFile(false);
    dispatch(fileUploadActions.setFile(null));

    if (!editDataForm?.requestId || !editDataForm?.fileName) {
      return;
    }

    const documentFolderPath = getDocumentFolder(editDataForm.requestId);

    getFormList(documentFolderPath)
      .unwrap()
      .then((response) => {
        const matchedDocument = getDocument(response, editDataForm.fileName);

        if (matchedDocument) {
          setUploadedFile({
            id: matchedDocument.documentId,
            name: editDataForm.fileName,
          });
        }
      });
  }, [editDataForm]);

  const handleUploadedFileRemove = useCallback(
    (value: boolean | undefined) => {
      setRemoveUploadedFile(Boolean(uploadedFile) && Boolean(value));
    },
    [uploadedFile]
  );

  const onFileRemove = useCallback((): void => {
    dispatch(fileUploadActions.setFile(null));
  }, []);

  const onFileRequest = useCallback((options: RcCustomRequestOptions): void => {
    const { file, onSuccess, onError } = options;
    const cb = (): void => {};
    const successCb = onSuccess || cb;
    const errorCb = onError || cb;
    const formData = new FormData();
    formData.append('file', file);

    if (file) {
      const filepath = generateFilepath('ITReq', (file as RcFile).name);
      dispatch(
        fileUploadActions.setFile({
          uploadedFile: file as RcFile,
          formData,
          filepath,
        })
      );
      successCb('Ok');
    } else {
      errorCb(new Error('file not uploaded'));
    }
  }, []);

  const uploadFile = useCallback(
    (reqFileId: string): Promise<DocumentFormUploadResponse> | null => {
      const { filepath, formData } = currentFileUpload;

      if (!filepath) {
        return null;
      }

      const body = getFormUploadFileMetaData(reqFileId, filepath);

      return formUpload({
        document: JSON.stringify(body),
        body: formData,
      }).unwrap();
    },
    [currentFileUpload]
  );

  const handleSubmit = useCallback(
    (
      values: ITRequestFormValues,
      formikHelpers: any,
      isModalAccepted = false
    ) => {
      const submitFormHandler = async (): Promise<void> => {
        const intCallNumber = `+${values.countryCode} ${values.intCallsNumber}`;
        const personalMobileValue = getPersonalMobileValue(
          values.itReqType,
          intCallNumber,
          values.contactNo
        );

        const usernameValue = getUsernameValue(
          values.itReqType,
          values.emailIdLeft,
          values.emailIdRight,
          chosenUser.empName
        );
        const accessoriesValue = getAccessoriesValue(
          values.itReqType,
          values.emailSignatureLeft,
          values.emailSignatureRight,
          values.accessories,
          itAccessories
        );

        const fileName = getFilepath(
          currentFileUpload.filepath,
          removeUploadedFile,
          editDataForm?.fileName
        );

        const body = {
          requestId: editDataForm?.requestId,
          reqItemId: values.itReqType as number,
          reqCapValue: values.simCardCreditLimit as number,
          isNewUser: values.isNewUser as number,
          modules: values.module,
          acctModules: values.accountsModule,
          organizations: [values.organization || ''],
          projects: values.projects,
          role: values.requiredRole,
          accessories: accessoriesValue,
          personalMobile: personalMobileValue,
          comments: values.remarks,
          fileName,
          empNumber: chosenUser.empNumber,
          username: usernameValue,
        };

        setDisableSaveForm(true);

        try {
          const {
            requestId: reqId,
            createdDate,
            statusId,
          } = await postITRequestForm(body).unwrap();

          dispatch(
            itRequisitionActions.setITRequisition({
              createdDate,
              requestId: reqId,
              reqStatusId: statusId,
            })
          );

          if (removeUploadedFile) {
            await deleteDocument(uploadedFile!.id).unwrap();
          }

          if (values.itReqType === ItRequisitionTypeCodes.EMAIL) {
            await uploadFile(String(reqId));
          }

          await submitForm(String(reqId)).unwrap();

          submitSuccessNotification(String(reqId));

          setDisableSaveForm(false);

          handleBackToListRequests();
        } catch (error) {
          const message = (error as RequestError)?.data;
          errorNotification(message);

          setDisableSaveForm(false);
        }
      };

      if (
        values.itReqType === ItRequisitionTypeCodes.EMAIL &&
        !isModalAccepted
      ) {
        setModalContext('submit');
        setIsModalOpened(true);
        return;
      }

      submitFormHandler();
    },
    [
      editDataForm,
      chosenUser,
      handleBackToListRequests,
      currentFileUpload,
      uploadFile,
      removeUploadedFile,
      uploadedFile,
      itAccessories,
      projects,
    ]
  );

  const validationSchema = useMemo(() => {
    const isFileAttached =
      !!currentFileUpload.filepath || (!!uploadedFile && !removeUploadedFile);
    const output = getItRequisitionFormSchema(itReqType, isFileAttached);
    return output;
  }, [itReqType, currentFileUpload, removeUploadedFile, uploadedFile]);

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema,
  });

  const handleSave = useCallback(
    async (isModalAccepted = false): Promise<void> => {
      if (
        formik.values.itReqType === ItRequisitionTypeCodes.EMAIL &&
        !isModalAccepted
      ) {
        setModalContext('save');
        setIsModalOpened(true);
        return;
      }

      const isFormValid = await validateForm(validationSchema, formik);

      if (!isFormValid) return;

      const intCallNumber = `+${formik.values.countryCode} ${formik.values.intCallsNumber}`;
      const personalMobileValue = getPersonalMobileValue(
        formik.values.itReqType,
        intCallNumber,
        formik.values.contactNo
      );

      const usernameValue = getUsernameValue(
        formik.values.itReqType,
        formik.values.emailIdLeft,
        formik.values.emailIdRight,
        chosenUser.empName
      );
      const accessoriesValue = getAccessoriesValue(
        formik.values.itReqType,
        formik.values.emailSignatureLeft,
        formik.values.emailSignatureRight,
        formik.values.accessories,
        itAccessories
      );

      const fileName = getFilepath(
        currentFileUpload.filepath,
        removeUploadedFile,
        editDataForm?.fileName
      );

      const body = {
        requestId: editDataForm?.requestId,
        reqItemId: formik.values.itReqType as number,
        reqCapValue: formik.values.simCardCreditLimit as number,
        isNewUser: formik.values.isNewUser as number,
        modules: formik.values.module,
        acctModules: formik.values.accountsModule,
        organizations: [formik.values.organization || ''],
        projects: formik.values.projects,
        role: formik.values.requiredRole,
        accessories: accessoriesValue,
        personalMobile: personalMobileValue,
        comments: formik.values.remarks,
        fileName,
        empNumber: chosenUser.empNumber,
        username: usernameValue,
      };

      setDisableSaveForm(true);

      try {
        const {
          requestId: reqId,
          createdDate,
          statusId,
        } = await postITRequestForm(body).unwrap();

        dispatch(
          itRequisitionActions.setITRequisition({
            createdDate,
            requestId: reqId,
            reqStatusId: statusId,
          })
        );

        if (removeUploadedFile) {
          await deleteDocument(uploadedFile!.id).unwrap();
        }

        if (formik.values.itReqType === ItRequisitionTypeCodes.EMAIL) {
          await uploadFile(String(reqId));
        }

        editSuccessNotification(String(reqId));

        setDisableSaveForm(false);

        refetch();
      } catch (error) {
        const message = (error as RequestError)?.data;
        errorNotification(message);

        setDisableSaveForm(false);
      }
    },
    [
      currentFileUpload,
      uploadFile,
      removeUploadedFile,
      uploadedFile,
      editDataForm,
      chosenUser,
      formik,
      validationSchema,
      itAccessories,
      projects,
    ]
  );

  const onModalCancel = (): void => {
    setIsModalOpened(false);
  };

  const onModalSubmit = (): void => {
    setIsModalOpened(false);

    const isModalAccepted = true;

    if (modalContext === 'save') {
      handleSave(isModalAccepted);
    } else if (modalContext === 'submit') {
      handleSubmit(formik.values, null, isModalAccepted);
    }
  };

  const changeItReqType = useCallback((value: number) => {
    formik.setFieldValue('itReqType', value);
    setItReqType(value);
  }, []);

  const validateCug = useCallback(
    (type: ItRequisitionTypeCodes) => {
      validateCugMobile(chosenUser.empNumber)
        .unwrap()
        .catch(() => {
          const message = getValidationCugError(type);
          errorNotification(message);

          changeItReqType(ItRequisitionTypeCodes.NEW_SIM_CARD);
        });
    },
    [chosenUser, changeItReqType]
  );

  const onItReqTypeChanged = useCallback(
    (value: number) => {
      setItReqType(value);

      const phone = isMobileNumberValid(chosenUser.mobile)
        ? chosenUser.mobile!
        : '';

      // on changing it requisition type it's need to clear all fields except chosen it requisition type
      setInitialFormValues({
        ...initialValuesForm,
        contactNo: phone,
        itReqType: value,
      });

      if (value === ItRequisitionTypeCodes.SIM_ADDON_REQUEST) {
        validateCug(ItRequisitionTypeCodes.SIM_ADDON_REQUEST);
      }

      if (value === ItRequisitionTypeCodes.LOST_SIM_CARD) {
        validateCug(ItRequisitionTypeCodes.LOST_SIM_CARD);
      }
    },
    [validateCug, chosenUser]
  );

  useEffect(() => {
    const { value: itReqTypeValue } = formik.getFieldMeta('itReqType');

    if (itReqTypeValue === ItRequisitionTypeCodes.SIM_ADDON_REQUEST) {
      // revalidate cug if selected user was changed
      validateCug(ItRequisitionTypeCodes.SIM_ADDON_REQUEST);
    }

    if (itReqTypeValue === ItRequisitionTypeCodes.LOST_SIM_CARD) {
      // revalidate cug if selected user was changed
      validateCug(ItRequisitionTypeCodes.LOST_SIM_CARD);
    }
  }, [validateCug, chosenUser]);

  useEffect(() => {
    if (!editDataForm?.empNumber) {
      return;
    }

    const restFields = parseSpecificFields(
      editDataForm.reqItemId,
      editDataForm.personalMobile,
      editDataForm.username,
      editDataForm.accessories
    );

    const { codeCountry, intCallNumber } = parseIntCalls(
      editDataForm.personalMobile
    );

    formik.setValues({
      itReqType: editDataForm.reqItemId,
      simCardCreditLimit: editDataForm.reqCapValue,
      document: null,
      isNewUser: editDataForm.isNewUser as IsNewUser,
      module: editDataForm.modules,
      accountsModule: editDataForm.acctModules,
      organization: editDataForm.organizations[0],
      projects: editDataForm.projects,
      requiredRole: editDataForm.role,
      remarks: editDataForm.comments,
      ...restFields,
      countryCode: codeCountry,
      intCallsNumber: intCallNumber,
    });

    setItReqType(editDataForm.reqItemId);

    getUser({ empNo: editDataForm.empNumber })
      .unwrap()
      .then((user) => {
        dispatch(userInfoActions.setSelectedUser(user));
      });
  }, [editDataForm]);

  useEffect(() => {
    // setting value to trigger validation
    const timeStamp = dayjs().toISOString();
    formik.setFieldValue('document', timeStamp);
  }, [currentFileUpload.filepath, removeUploadedFile]);

  useEffect(() => {
    if (chosenUser) {
      const splittedName = splitUserName(chosenUser.empName);
      setSplittedUserName(splittedName);

      if (
        editDataForm &&
        chosenUser.empNumber &&
        chosenUser.empNumber !== editDataForm.empNumber
      ) {
        formik.setFieldValue('contactNo', chosenUser.mobile || '');
      }
    }
  }, [chosenUser, editDataForm]);

  useEffect(() => {
    const { organization } = formik.values;

    if (organization) {
      getProjects(organization)
        .unwrap()
        .then((projectsResp) => {
          const transformedProjects = transformProjectsToOptions(projectsResp);
          setProjects(transformedProjects);
        })
        .catch(() => {
          setProjects([]);
        });
    }
  }, [formik.values.organization]);

  const handleSearchEmpByNumber = useCallback(
    (empNumber?: string): void => {
      const validEmpNumber = empNumber || currentUser.empNumber;

      if (!validEmpNumber) {
        return;
      }

      getUser({ empNo: validEmpNumber })
        .unwrap()
        .then((user) => setSelectedEmpUser(user));
    },
    [currentUser]
  );

  const handleApplySelectedUser = useCallback(() => {
    if (selectedEmpUser) {
      dispatch(userInfoActions.setSelectedUser(selectedEmpUser));
      // reset form after new user selected
      formik.setFieldValue('emailIdLeft', initialValuesForm.emailIdLeft);
      formik.setFieldValue('emailIdRight', initialValuesForm.emailIdRight);
      formik.setFieldValue(
        'emailSignatureLeft',
        initialValuesForm.emailSignatureLeft
      );
      formik.setFieldValue(
        'emailSignatureRight',
        initialValuesForm.emailSignatureRight
      );
      formik.setFieldValue(
        'emailSignatureRight',
        initialValuesForm.emailSignatureRight
      );
    }
  }, [dispatch, formik, selectedEmpUser]);

  const simCapValuesOptions = useMemo(() => {
    return transformDictToOptionsNum(simCapValues);
  }, [simCapValues]);
  const itAccessoriesOptions = useMemo(() => {
    return transformDictToOptions(itAccessories);
  }, [itAccessories]);

  const showDeleteRequestPopup = (): void => {
    setShowDeleteNotification(true);
  };

  const handleDeleteRequest = useCallback((): void => {
    if (!editDataForm?.requestId) {
      return;
    }

    const reqId = String(editDataForm.requestId);

    deleteRequest(reqId)
      .unwrap()
      .then(() => {
        deleteSuccessNotification(reqId);

        handleBackToListRequests();
      })
      .catch(({ data }) => {
        errorNotification(data);
      });
  }, [editDataForm, handleBackToListRequests]);

  const handleCancel = useCallback(() => {
    if (!editDataForm?.requestId) {
      return;
    }

    const reqId = String(editDataForm.requestId);

    cancelRequest(reqId)
      .unwrap()
      .then(() => {
        cancelSuccessNotification(reqId);

        handleBackToListRequests();
      })
      .catch(({ data }) => {
        errorNotification(data);
      });
  }, [handleBackToListRequests, editDataForm]);

  const handleFooterAction = useCallback(
    (action: FooterActions) => {
      if (action === FooterActions.CANCEL) {
        handleCancel();
      }

      if (action === FooterActions.SAVE_AS_DRAFT) {
        handleSave();
      }

      if (action === FooterActions.DELETE_REQUEST) {
        showDeleteRequestPopup();
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }
    },
    [handleBackToListRequests, handleSave, handleCancel]
  );

  const handleModuleChange = useCallback((value: string[]) => {
    if (!value.includes(String(ModuleOptions.PURCHASE))) {
      formik.setFieldValue('requiredRole', null);
    }

    if (!value.includes(String(ModuleOptions.ACCOUNTS))) {
      formik.setFieldValue('accountsModule', null);
    }
  }, []);

  const isOpenStatus =
    !editDataForm || editDataForm.statusId === ItRequisitionStatusCodes.OPEN;

  const formType = getEditFormType(isOpenStatus);

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        handleSubmit={formik.handleSubmit}
        userInfo={chosenUser}
        selectedUser={selectedEmpUser}
        requisitionInfo={requisitionInfo}
        descriptionAlert={descriptionAlert}
        onFooterAction={handleFooterAction}
        handleSearchEmpByNumber={handleSearchEmpByNumber}
        handleApplySelectedUser={handleApplySelectedUser}
        showSelectEmployee
        disabledSaveForm={disabledSaveForm}
        formType={formType}
      >
        <FormItems>
          <RequestDetailsSection
            simCapValues={simCapValuesOptions}
            itAccessories={itAccessoriesOptions}
            requisitionItems={itReqTypeOptions}
            splittedUserName={splittedUserName}
            onFileRequest={onFileRequest}
            label="Request Details"
            formik={formik}
            onItReqTypeChange={onItReqTypeChanged}
            onFileRemove={onFileRemove}
            uploadedFile={uploadedFile}
            onUploadedFileRemove={handleUploadedFileRemove}
            organizations={organizations}
            projects={projects}
            onModuleChange={handleModuleChange}
            isModalOpened={isModalOpened}
            onModalCancel={onModalCancel}
            onModalSubmit={onModalSubmit}
          />
        </FormItems>
      </MainForm>
      <RequiredNotification
        title="Delete Request"
        message={`Are you sure you want to delete request no.${requestId}?`}
        isModalOpen={showDeleteNotification}
        textSuccessBtn="Yes, Delete"
        textCancelBtn="No, Thanks"
        handleCancel={() => setShowDeleteNotification(false)}
        handleSuccess={handleDeleteRequest}
        isDeleteNotification
      />
    </Flex>
  );
};

export default ITRequestEditForm;
